import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Calendar, TrendingUp, TrendingDown, Thermometer, 
  CloudRain, Sun, BookOpen, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoricalWeather({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchHistoricalData();
  }, [lat, lon, selectedDate]);

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/weather/historical`, {
        params: { lat, lon, date: dateStr }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch historical data:", error);
    } finally {
      setLoading(false);
    }
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-48 mb-4"></div>
        <div className="h-32 bg-white/10 rounded"></div>
      </div>
    );
  }

  if (!data?.success) {
    return null;
  }

  const { almanac, historical_records, fun_facts } = data;
  const averageRange = Math.max(0, (almanac.average_high || 0) - (almanac.average_low || 0));
  const recordRange = Math.max(0, (almanac.record_high?.temp || 0) - (almanac.record_low?.temp || 0));
  const warmestRecord = [...historical_records].sort((a, b) => b.high_temp - a.high_temp)[0];
  const coolestRecord = [...historical_records].sort((a, b) => a.low_temp - b.low_temp)[0];
  const clearYears = historical_records.filter((record) => String(record.conditions || '').toLowerCase().includes('clear')).length;
  const rainyYears = historical_records.filter((record) => /rain|storm|shower/i.test(record.conditions || '')).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
            <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
          </div>
          <div>
            <h3 className="font-bold">Weather Almanac</h3>
            <p className="text-sm opacity-60">This Day in History</p>
          </div>
        </div>
        
        {/* Date Navigator */}
        <div className="flex items-center gap-2">
          <Button 
            data-testid="almanac-previous-day-button"
            variant="ghost" 
            size="sm" 
            onClick={() => changeDate(-1)}
            className="rounded-lg"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium px-3">
            {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          <Button 
            data-testid="almanac-next-day-button"
            variant="ghost" 
            size="sm" 
            onClick={() => changeDate(1)}
            className="rounded-lg"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Almanac Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="almanac-stats-grid">
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-average-high-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-red-400" />
            <span className="text-xs opacity-60">Avg High</span>
          </div>
          <p className="text-2xl font-bold">{almanac.average_high}°</p>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-average-low-card">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-blue-400" />
            <span className="text-xs opacity-60">Avg Low</span>
          </div>
          <p className="text-2xl font-bold">{almanac.average_low}°</p>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-record-high-card">
          <div className="flex items-center gap-2 mb-1">
            <Thermometer className="w-4 h-4 text-orange-400" />
            <span className="text-xs opacity-60">Record High</span>
          </div>
          <p className="text-xl font-bold">{almanac.record_high.temp}°</p>
          <p className="text-xs opacity-50">{almanac.record_high.year}</p>
        </div>
        
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-record-low-card">
          <div className="flex items-center gap-2 mb-1">
            <Thermometer className="w-4 h-4 text-cyan-400" />
            <span className="text-xs opacity-60">Record Low</span>
          </div>
          <p className="text-xl font-bold">{almanac.record_low.temp}°</p>
          <p className="text-xs opacity-50">{almanac.record_low.year}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="almanac-derived-insights-grid">
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-average-range-card">
          <p className="text-xs opacity-60 mb-1">Avg Range</p>
          <p className="text-xl font-bold">{averageRange}°</p>
        </div>
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-record-spread-card">
          <p className="text-xs opacity-60 mb-1">Record Spread</p>
          <p className="text-xl font-bold">{recordRange}°</p>
        </div>
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-clear-years-card">
          <p className="text-xs opacity-60 mb-1">Clear Years</p>
          <p className="text-xl font-bold">{clearYears}</p>
        </div>
        <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`} data-testid="almanac-rain-years-card">
          <p className="text-xs opacity-60 mb-1">Wet Years</p>
          <p className="text-xl font-bold">{rainyYears}</p>
        </div>
      </div>

      {/* Historical Records */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold mb-3 opacity-70">Past Years on This Day</h4>
        <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="almanac-records-list">
          {historical_records.map((record, idx) => (
            <div 
              key={`item-${idx}`}
              data-testid={`almanac-record-${record.year}`}
              className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
            >
              <span className="font-medium">{record.year}</span>
              <div className="flex items-center gap-4">
                <span className="text-red-400">{record.high_temp}°</span>
                <span className="text-blue-400">{record.low_temp}°</span>
                <span className="text-sm opacity-60 w-24 text-right">{record.conditions}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="almanac-extremes-grid">
        {warmestRecord && (
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`} data-testid="almanac-warmest-visible-record">
            <h4 className="text-sm font-semibold text-red-400 mb-2">Warmest visible year</h4>
            <p className="text-sm opacity-80">{warmestRecord.year}: {warmestRecord.high_temp}° high · {warmestRecord.conditions}</p>
          </div>
        )}
        {coolestRecord && (
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`} data-testid="almanac-coolest-visible-record">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">Coolest visible year</h4>
            <p className="text-sm opacity-80">{coolestRecord.year}: {coolestRecord.low_temp}° low · {coolestRecord.conditions}</p>
          </div>
        )}
      </div>

      {/* Fun Facts */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`} data-testid="almanac-fun-facts-card">
        <h4 className="text-sm font-semibold mb-2 text-amber-500">Did You Know?</h4>
        <ul className="space-y-1">
          {fun_facts.map((fact, idx) => (
            <li key={`item-${idx}`} className="text-sm opacity-80">• {fact}</li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}
